import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  ReactFlow,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  addEdge,
  type Connection,
  type Edge,
  type NodeChange,
  type EdgeChange,
  ReactFlowProvider,
  type Node,
  BackgroundVariant,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { Sidebar } from "./Sidebar";
import { TriggerNode, ActionNode, ConditionNode, WorkflowNode, AINode, HumanNode } from "./nodes/CustomNodes";
import { useCreateWorkflowMutation, useGetWorkflowQuery, useUpdateWorkflowMutation } from "@/services/WorkFlowBuilderApi";
import { NodeCreate, EdgeCreate, WorkflowNodeType, WorkflowCreate } from "@/app/types/workflow";
import { validateWorkflowVariables } from "./workflowVariableUtils";

const nodeTypes = {
  trigger: TriggerNode,
  action: ActionNode,
  condition: ConditionNode,
  workflow: WorkflowNode,
  ai: AINode,
  human: HumanNode,
};

const initialNodes: Node[] = [];

const getId = () => `dndnode_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;

import { PropertiesPanel } from "./PropertiesPanel";
import { AlertCircle } from "lucide-react";
import { useSearchParams, useNavigate, UNSAFE_NavigationContext as NavigationContext } from "react-router-dom";
import { useContext } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/AlertDialog";
import { useToast } from "@/components/ui/UseToast";

const WorkflowBuilderContent = () => {
  const reactFlowWrapper = useRef<HTMLDivElement>(null);
  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([]);
  const [reactFlowInstance, setReactFlowInstance] = useState<any>(null);
  const [selectedNode, setSelectedNode] = useState<Node | null>(null);

  const [selectedEdge, setSelectedEdge] = useState<Edge | null>(null);
  const [createWorkflow] = useCreateWorkflowMutation();
  const [updateWorkflow] = useUpdateWorkflowMutation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const workflowId = searchParams.get("id");
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [workflowName, setWorkflowName] = useState("");
  const [showNameError, setShowNameError] = useState(false);
  const { toast } = useToast();
  const [isCreating, setIsCreating] = useState(false);

  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const { navigator } = useContext(NavigationContext);
  const [showBlockerDialog, setShowBlockerDialog] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<{ retry: () => void } | null>(null);

  useEffect(() => {
    if (!hasUnsavedChanges) return;

    const push = navigator.push;
    const replace = navigator.replace;

    const handleNavigation = (args: any[], originalFunction: Function) => {
      setShowBlockerDialog(true);
      setPendingNavigation({
        retry: () => {
          setShowBlockerDialog(false);
          setPendingNavigation(null);
          originalFunction(...args);
        }
      });
    };

    // simplified blocking logic for v6 BrowserRouter
    navigator.push = (...args: any[]) => handleNavigation(args, push);
    navigator.replace = (...args: any[]) => handleNavigation(args, replace);

    return () => {
      navigator.push = push;
      navigator.replace = replace;
    };
  }, [hasUnsavedChanges, navigator]);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasUnsavedChanges]);

  // Wrappers to track changes
  const onNodesChangeWrapped = useCallback(
    (changes: NodeChange[]) => {
      onNodesChange(changes);
      // Only mark as dirty if the change is NOT just a selection change
      if (changes.some((c) => c.type !== "select")) {
        setHasUnsavedChanges(true);
      }
    },
    [onNodesChange]
  );

  const onEdgesChangeWrapped = useCallback(
    (changes: EdgeChange[]) => {
      onEdgesChange(changes);
      if (changes.some((c) => c.type !== "select")) {
        setHasUnsavedChanges(true);
      }
    },
    [onEdgesChange]
  );

  const handleSetNodes = useCallback(
    (value: React.SetStateAction<Node[]>) => {
      setNodes(value);
      setHasUnsavedChanges(true);
    },
    [setNodes]
  );

  const handleSetWorkflowName = useCallback(
    (value: React.SetStateAction<string>) => {
      setWorkflowName(value);
      setHasUnsavedChanges(true);
    },
    [setWorkflowName]
  );

  const { data: workflowData, error: workflowError, isError } = useGetWorkflowQuery(Number(workflowId), {
    skip: !workflowId,
  });

  useEffect(() => {
    if (isError && workflowId) {
      setShowErrorDialog(true);
    }
  }, [isError, workflowId]);

  useEffect(() => {
    if (workflowData) {
      // Set workflow name from loaded data
      setWorkflowName(workflowData.name || "");

      // Map API response to React Flow nodes and edges
      const mappedNodes: Node[] = workflowData.nodes.map((node) => ({
        id: node.node_key,
        type: node.node_type,
        position: node.position as { x: number; y: number },
        data: { label: node.label, config: node.config },
      }));

      const mappedEdges: Edge[] = workflowData.edges.map((edge) => ({
        id: `e${edge.source_node_key}-${edge.target_node_key}`,
        source: edge.source_node_key,
        target: edge.target_node_key,
        label: edge.edge_label,
        sourceHandle: edge.edge_label === "true" || edge.edge_label === "false" ? edge.edge_label : undefined,
      }));

      setNodes(mappedNodes);
      setEdges(mappedEdges);
    }
  }, [workflowData, setNodes, setEdges]);

  const onConnect = useCallback(
    (params: Connection) => {
      setEdges((eds) => addEdge(params, eds));
      setHasUnsavedChanges(true);
    },
    [setEdges]
  );

  const onDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
  }, []);

  const onDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();

      const dataString = event.dataTransfer.getData("application/reactflow");

      if (!dataString) {
        return;
      }

      let nodeData: WorkflowNodeType;
      try {
        nodeData = JSON.parse(dataString);
      } catch (e) {
        console.error("Failed to parse node data", e);
        return;
      }

      const { node_type: type, label, config } = nodeData;

      if (type === "trigger" && nodes.some((n) => n.type === "trigger")) {
        alert("Only one trigger node is allowed.");
        return;
      }

      const position = reactFlowInstance.screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      });

      const newNode: Node = {
        id: getId(),
        type,
        position,
        data: { label, config },
      };

      setNodes((nds) => nds.concat(newNode));
      setHasUnsavedChanges(true);
    },
    [reactFlowInstance, setNodes, nodes]
  );

  const onNodeClick = useCallback((_: React.MouseEvent, node: Node) => {
    setSelectedNode(node);
    setSelectedEdge(null);
  }, []);

  const onEdgeClick = useCallback((_: React.MouseEvent, edge: Edge) => {
    setSelectedEdge(edge);
    setSelectedNode(null);
  }, []);

  const onPaneClick = useCallback(() => {
    setSelectedNode(null);
    setSelectedEdge(null);
  }, []);

  const runWorkflow = useCallback(() => {
    const startNode = nodes.find((n) => n.type === "trigger");
    if (!startNode) {
      alert("No trigger node found!");
      return;
    }

    const visited = new Set<string>();
    const queue: Node[] = [startNode];
    const executionPath: string[] = [];

    while (queue.length > 0) {
      const currentNode = queue.shift();
      if (!currentNode || visited.has(currentNode.id)) continue;

      visited.add(currentNode.id);
      executionPath.push(`${currentNode.type}: ${currentNode.data.label}`);

      const outgoingEdges = edges.filter((e) => e.source === currentNode.id);

      for (const edge of outgoingEdges) {
        const targetNode = nodes.find((n) => n.id === edge.target);
        if (targetNode) {
          queue.push(targetNode);
        }
      }
    }

    alert(`Execution Path:\n${executionPath.join(" -> ")}`);
  }, [nodes, edges]);

  const deleteNode = useCallback(
    (nodeId: string) => {
      setNodes((nds) => nds.filter((node) => node.id !== nodeId));
      setEdges((eds) =>
        eds.filter((edge) => edge.source !== nodeId && edge.target !== nodeId)
      );
      setSelectedNode(null);
      setHasUnsavedChanges(true);
    },
    [setNodes, setEdges]
  );

  const deleteEdge = useCallback(
    (edgeId: string) => {
      setEdges((eds) => eds.filter((edge) => edge.id !== edgeId));
      setSelectedEdge(null);
      setHasUnsavedChanges(true);
    },
    [setEdges]
  );

  const handleAIWorkflowGenerated = useCallback(
    (workflow: WorkflowCreate) => {
      // Set the workflow name
      if (workflow.name) {
        setWorkflowName(workflow.name);
        setShowNameError(false);
      }

      // Convert AI-generated nodes to React Flow nodes
      const aiNodes: Node[] = workflow.nodes?.map((node) => ({
        id: node.node_key,
        type: node.node_type,
        position: node.position as { x: number; y: number },
        data: { label: node.label, config: node.config },
      })) || [];

      // Convert AI-generated edges to React Flow edges
      const aiEdges: Edge[] = workflow.edges?.map((edge) => ({
        id: `e${edge.source_node_key}-${edge.target_node_key}`,
        source: edge.source_node_key,
        target: edge.target_node_key,
        label: edge.edge_label,
        sourceHandle: edge.edge_label === "true" || edge.edge_label === "false" ? edge.edge_label : undefined,
      })) || [];

      // Apply the AI-generated workflow to the canvas
      setNodes(aiNodes);
      setEdges(aiEdges);
      setHasUnsavedChanges(true);

      // Clear any selections
      setSelectedNode(null);
      setSelectedEdge(null);
    },
    [setNodes, setEdges, setWorkflowName, setShowNameError]
  );

  const handleCreateWorkflow = async (status?: string) => {
    if (!workflowName.trim()) {
      setSelectedNode(null); // Clear selection to show workflow settings
      setSelectedEdge(null);
      setShowNameError(true); // Show error under input
      return;
    }

    // Validate dynamic variables in all nodes
    const variableValidation = validateWorkflowVariables(nodes, edges);
    if (!variableValidation.isValid) {
      const firstError = variableValidation.errors[0];
      const errorNode = nodes.find(n => n.id === firstError.nodeId);
      if (errorNode) {
        setSelectedNode(errorNode);
      }
      toast({
        variant: "destructive",
        title: "Invalid Dynamic Variables",
        description: (
          <div className="space-y-1">
            {variableValidation.errors.map((err) => (
              <div key={err.nodeLabel}>
                • {err.nodeLabel}: {err.message}
              </div>
            ))}
          </div>
        ),
        duration: 15000,
      });
      return;
    }

    // Validate nodes
    for (const node of nodes) {
      const config = node.data.config as any;

      // Validate Action nodes
      if (node.type === "action") {
        if (config?.action_type === "sub_workflow") {
          if (!config.action_config?.workflow_id) {
            toast({
              variant: "destructive",
              title: "Validation Error",
              description: `Sub Workflow node "${node.data.label}" must have a workflow selected.`,
            });
            setSelectedNode(node);
            return;
          }
        } else if (config?.action_type === "read_form_responses" || config?.action_type === "update_form_status") {
          if (!config.action_config?.form_id) {
            toast({
              variant: "destructive",
              title: "Validation Error",
              description: `Action node "${node.data.label}" must have a form selected.`,
            });
            setSelectedNode(node);
            return;
          }
        } else if (config?.action_type === "email") {
          if (!config.action_config?.to) {
            toast({
              variant: "destructive",
              title: "Validation Error",
              description: `Email action node "${node.data.label}" must have a recipient email.`,
            });
            setSelectedNode(node);
            return;
          }
        } else if (config?.action_type === "whatsapp") {
          if (!config.action_config?.number && !config.action_config?.to) {
            toast({
              variant: "destructive",
              title: "Validation Error",
              description: `WhatsApp action node "${node.data.label}" must have a recipient phone number.`,
            });
            setSelectedNode(node);
            return;
          }
        } else if (config?.action_type === "ticket_creation" || config?.action_type === "task_creation") {
          if (!config.action_config?.title) {
            toast({
              variant: "destructive",
              title: "Validation Error",
              description: `${config.action_type === "ticket_creation" ? "Ticket" : "Task"} creation node "${node.data.label}" must have a title.`,
            });
            setSelectedNode(node);
            return;
          }
        }
      }

      // Validate Trigger nodes
      if (node.type === "trigger") {
        if (config?.trigger_type === "event" && config?.event_trigger_type === "form_submission") {
          if (!config.event_config?.source_id) {
            toast({
              variant: "destructive",
              title: "Validation Error",
              description: `Trigger node "${node.data.label}" must have a form selected.`,
            });
            setSelectedNode(node);
            return;
          }
        }
      }
    }

    setIsCreating(true); // Show loader

    const isEditMode = !!workflowId; // Check if we're editing an existing workflow

    const workflowNodes: NodeCreate[] = nodes.map((node) => {
      const existingConfig = (node.data.config as any) || {};
      let config: any = {};

      if (node.type === "trigger") {
        config = {
          trigger_type: existingConfig.trigger_type || "event",
          event_trigger_type: existingConfig.event_trigger_type || null, // Default or from UI
          event_config: existingConfig.event_config || {}, // Default or from UI
          ...existingConfig,
        };
      } else if (node.type === "action") {
        config = {
          action_type: existingConfig.action_type,
          action_config: existingConfig.action_config,
        };
      } else if (node.type === "condition") {
        config = { ...existingConfig };
      } else {
        config = { ...existingConfig };
      }

      return {
        node_key: node.id,
        node_type: node.type || "default",
        label: (node.data.label as string) || "",
        position: node.position,
        config: config,
      };
    });

    const workflowEdges: EdgeCreate[] = edges.map((edge) => ({
      source_node_key: edge.source,
      target_node_key: edge.target,
      edge_label:
        edge.sourceHandle === "true"
          ? "true"
          : edge.sourceHandle === "false"
            ? "false"
            : undefined,
    }));

    try {
      if (isEditMode) {
        // Update existing workflow
        const updateBody: any = {
          name: workflowName.trim(),
          description: "Updated via Workflow Builder",
          nodes: workflowNodes,
          edges: workflowEdges,
        };
        if (status) updateBody.status = status;

        await updateWorkflow({
          id: Number(workflowId),
          body: updateBody,
        }).unwrap();

        toast({
          variant: "success",
          title: "Success",
          description: "Workflow updated successfully!",
        });
      } else {
        // Create new workflow
        const createBody: any = {
          name: workflowName.trim(),
          description: "Created via Workflow Builder",
          nodes: workflowNodes,
          edges: workflowEdges,
        };
        if (status) createBody.status = status;

        await createWorkflow(createBody).unwrap();

        toast({
          variant: "success",
          title: "Success",
          description: "Workflow created successfully!",
        });
      }

      setWorkflowName("");
      setHasUnsavedChanges(false);

      // Redirect to workflow listing
      // Use setTimeout to ensure the state update processes before navigation happens
      // (avoiding the blocking interceptor)
      setTimeout(() => {
        navigate("/admin/workflow-management");
      }, 0);
    } catch (error) {
      console.error(`Failed to ${isEditMode ? 'update' : 'create'} workflow:`, error);
      toast({
        variant: "destructive",
        title: "Error",
        description: `Failed to ${isEditMode ? 'update' : 'create'} workflow. ${(error as any)?.data?.message || "Unknown error"}.`,
      });
    } finally {
      setIsCreating(false); // Hide loader
    }
  };

  return (
    <>
      <AlertDialog open={showErrorDialog} onOpenChange={setShowErrorDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <div className="flex items-center gap-3">
              <AlertCircle className="h-6 w-6 text-red-600" />
              <AlertDialogTitle>Workflow Not Found</AlertDialogTitle>
            </div>
            <AlertDialogDescription>
              Failed to load workflow with ID {workflowId}. The workflow may not exist or you may not have permission to access it.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => {
              setShowErrorDialog(false);
              navigate("/admin/workflow-management");
            }}>
              Go to Workflows
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={showBlockerDialog} onOpenChange={setShowBlockerDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <div className="flex items-center gap-3">
              <AlertCircle className="h-6 w-6 text-amber-600" />
              <AlertDialogTitle>Unsaved Changes</AlertDialogTitle>
            </div>
            <AlertDialogDescription>
              You have unsaved changes. Are you sure you want to leave? Your changes will be lost.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="w-30" onClick={() => {
              setShowBlockerDialog(false);
              setPendingNavigation(null);
            }}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => {
              if (pendingNavigation) {
                pendingNavigation.retry();
              }
            }}>Continue</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <div className="flex h-[calc(100vh-64px)] w-full flex-col">
        <div className="flex flex-grow h-full">
          <Sidebar onCreateWorkflow={handleCreateWorkflow} isCreating={isCreating} isEditMode={!!workflowId} onAIWorkflowGenerated={handleAIWorkflowGenerated} />
          <div className="flex-grow h-full" ref={reactFlowWrapper}>
            <ReactFlow
              nodes={nodes}
              edges={edges}
              onNodesChange={onNodesChangeWrapped}
              onEdgesChange={onEdgesChangeWrapped}
              onConnect={onConnect}
              onInit={setReactFlowInstance}
              onDrop={onDrop}
              onDragOver={onDragOver}
              onNodeClick={onNodeClick}
              onEdgeClick={onEdgeClick}
              onPaneClick={onPaneClick}
              nodeTypes={nodeTypes}
              fitView
            >
              <Controls />
              <Background variant={BackgroundVariant.Dots} gap={12} size={1} />
            </ReactFlow>
          </div>
          <PropertiesPanel
            selectedNode={selectedNode}
            selectedEdge={selectedEdge}
            nodes={nodes}
            edges={edges}
            setNodes={handleSetNodes}
            onDelete={deleteNode}
            onDeleteEdge={deleteEdge}
            workflowName={workflowName}
            setWorkflowName={handleSetWorkflowName}
            showNameError={showNameError}
            setShowNameError={setShowNameError}
            onCreateWorkflow={handleCreateWorkflow}
            onRunWorkflow={runWorkflow}
            isCreating={isCreating}
          />
        </div>
      </div>
    </>
  );
};

export const WorkflowBuilder = () => {
  return (
    <ReactFlowProvider>
      <WorkflowBuilderContent />
    </ReactFlowProvider>
  );
};
